"use client";

import { useEffect } from "react";

const highlightAttribute = "data-pricing-highlight";
const featureTargetPrefix = "features-";

function clearHighlights() {
  document
    .querySelectorAll(`[${highlightAttribute}="true"]`)
    .forEach((element) => element.removeAttribute(highlightAttribute));
}

function activateFeatureHighlight(targetId: string, planId?: string): IntersectionObserver | null {
  if (!targetId.startsWith(featureTargetPrefix)) return null;

  const target = document.getElementById(targetId);
  if (!target?.hasAttribute("data-pricing-feature-group")) return null;

  clearHighlights();
  target.setAttribute(highlightAttribute, "true");

  if (planId) {
    target
      .querySelectorAll(`[data-pricing-plan-id="${planId}"]`)
      .forEach((element) => element.setAttribute(highlightAttribute, "true"));
  }

  const observer = new IntersectionObserver(
    ([entry]) => {
      if (!entry?.isIntersecting) {
        clearHighlights();
        observer.disconnect();
      }
    },
    { threshold: 0.05 },
  );

  observer.observe(target);
  return observer;
}

export default function PricingFeatureAnchorHighlighter() {
  useEffect(() => {
    let observer: IntersectionObserver | null = null;

    const activate = (targetId: string, planId?: string) => {
      observer?.disconnect();
      observer = activateFeatureHighlight(targetId, planId);
    };

    const activateFromHash = () => {
      const targetId = decodeURIComponent(window.location.hash.slice(1));
      window.setTimeout(() => activate(targetId), 80);
    };

    const activateFromClick = (event: MouseEvent) => {
      const trigger = (event.target as Element | null)?.closest<HTMLAnchorElement>(
        "a[data-pricing-feature-target]",
      );
      const targetId = trigger?.dataset.pricingFeatureTarget;
      if (!targetId) return;

      window.setTimeout(() => {
        activate(targetId, trigger.dataset.pricingPlanTarget);
      }, 180);
    };

    document.addEventListener("click", activateFromClick);
    window.addEventListener("hashchange", activateFromHash);
    activateFromHash();

    return () => {
      document.removeEventListener("click", activateFromClick);
      window.removeEventListener("hashchange", activateFromHash);
      observer?.disconnect();
      clearHighlights();
    };
  }, []);

  return null;
}
